import GoogleMapReact, { Map } from "google-map-react";
import React, { useEffect, useState } from "react";

import { GOOGLE_MAPS_API_KEY } from "@/shared/constants";

import { usePosition } from "@/hooks/usePosition";

import {
    GeocodedResultsProps,
    LatLngLiteralProps,
    LocationProps,
} from "@/interfaces/locations/location-interface";

import { InStoreMarker } from "./in-store-marker";
import styles from "./instore.module.scss";

type Props = {
    locations: Array<LocationProps>;
    geocodedResults?: GeocodedResultsProps;
    onLatLngChanged: (center: LatLngLiteralProps, bounds: LatLngLiteralProps) => void;
    onBoundsChanged: (bounds: LatLngLiteralProps) => void;
};

type SelectedLocationProps = {
    isActive: boolean;
    location: LocationProps;
};

export const InStoreMap: React.FC<Props> = (props: Props) => {
    const { geocodedResults, locations, onLatLngChanged, onBoundsChanged } = props;
    const inactiveLocation: SelectedLocationProps = {
        isActive: false,
        location: null,
    };
    const { lat, lng, zoom } = usePosition(true);
    const [map, setMap] = useState<Map>(null);
    const [centerPosition, setCenterPosition] = useState<LatLngLiteralProps>({
        lat: 0,
        lng: 0,
    });
    const [activeLocation, setActiveLocation] = useState<SelectedLocationProps>(inactiveLocation);
    const [previousLocation, setPreviousLocation] =
        useState<SelectedLocationProps>(inactiveLocation);

    const setInfoWindowLocation = (location: LocationProps) => {
        setPreviousLocation({
            isActive: false,
            location: activeLocation.location,
        });
        setActiveLocation({
            isActive: true,
            location,
        });
    };

    const onMapClickCloseWindow = () => {
        setActiveLocation(inactiveLocation);
    };

    useEffect(() => {
        if (geocodedResults?.location?.lat && geocodedResults?.location?.lng) {
            setCenterPosition({
                lat: geocodedResults.location.lat,
                lng: geocodedResults.location.lng,
            });
        }
    }, [geocodedResults?.location?.lat, geocodedResults?.location?.lng]);

    useEffect(() => {
        setCenterPosition({
            lat: lat,
            lng: lng,
        });
    }, [lat, lng]);

    return (
        <div className={styles.instoreMapContainer}>
            <GoogleMapReact
                bootstrapURLKeys={{
                    key: GOOGLE_MAPS_API_KEY,
                    libraries: ["places"],
                }}
                center={[centerPosition.lat, centerPosition.lng]}
                defaultZoom={5}
                zoom={zoom}
                yesIWantToUseGoogleMapApiInternals={true}
                onGoogleApiLoaded={({ map }): Map => {
                    setMap(map);
                    onBoundsChanged(map.getBounds().getNorthEast());
                }}
                onDragEnd={() => onLatLngChanged(map.getCenter(), map.getBounds().getNorthEast())}
                onZoomAnimationEnd={() => onBoundsChanged(map.getBounds().getNorthEast())}
                onClick={onMapClickCloseWindow}
            >
                {locations &&
                    locations.map((location) => {
                        return (
                            location?.longitude &&
                            location?.latitude && (
                                <InStoreMarker
                                    lat={location.latitude}
                                    lng={location.longitude}
                                    location={location}
                                    key={location.id}
                                    onClick={() => {
                                        setInfoWindowLocation(location);
                                        setCenterPosition({
                                            lat: location.latitude,
                                            lng: location.longitude,
                                        });
                                    }}
                                    activeLocation={activeLocation}
                                    previousLocation={previousLocation}
                                />
                            )
                        );
                    })}
            </GoogleMapReact>
        </div>
    );
};
